// Central content + media for the SportMate.ai marketing site
export const CONTACT_EMAIL = "hello@sportmate.ai";
export const APP_URL = "https://app.sportmate.ai";
export const PORTAL_URL = "https://portal.sportmate.ai";

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1487466365202-1afdb86c764e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000&ixlib=rb-4.1.0",
  people: "https://images.unsplash.com/photo-1622659097509-4d56de14539e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&ixlib=rb-4.1.0",
  operations: "https://images.unsplash.com/photo-1775803157852-21f21c12b127?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&ixlib=rb-4.1.0",
  grow: "https://images.unsplash.com/photo-1665413811870-5b29a250f64a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&ixlib=rb-4.1.0",
  app: "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&ixlib=rb-4.1.0",
  advisory: "https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&ixlib=rb-4.1.0",
};

export const HERO = {
  overline: "Efficient · Sustainable · Integrated",
  title: ["Helping clubs", "do more —", "for their future."],
  body:
    "SportMate.ai combines practical technology, volunteer support and experienced professional advice to help community sports clubs save time, reduce costs and create better experiences.",
  primary: { label: "How we can help your club", type: "mail" },
  secondary: { label: "Explore volunteer support", href: "#volunteer-app" },
};

export const PROBLEM = {
  overline: "The reality",
  title: "Running a club is rewarding — but increasingly demanding",
  body:
    "Volunteers face growing administrative workloads. Clubs must manage rising costs, complex technology, governance responsibilities and ageing facilities — often without access to the specialist experience they need.",
  punch: "SportMate.ai brings that support together in one place.",
  stats: [
    { k: "Time", v: "Less admin for volunteers" },
    { k: "Cost", v: "Lower bills and better value" },
    { k: "Future", v: "Stronger facilities and finances" },
  ],
};

export const PILLARS = [
  {
    key: "people",
    num: "01",
    title: "Support your people",
    blurb:
      "Give volunteers the tools, guidance and support they need — so their time goes further and every child gets a better experience.",
    examples: [
      "The SportMate.ai volunteer app",
      "Coach & manager guidance",
      "Cutting repetitive admin",
      "Volunteer recruitment & retention",
      "Member communications",
    ],
    image: IMAGES.people,
  },
  {
    key: "operations",
    num: "02",
    title: "Run your club better",
    blurb:
      "Bring order to the day-to-day — sensible technology, lower costs and the governance every modern club is expected to have.",
    examples: [
      "Technology, websites & booking",
      "Energy, insurance & supplier reviews",
      "Governance, policy & data protection",
      "Finance & forecasting",
      "Facility & booking optimisation",
    ],
    image: IMAGES.operations,
  },
  {
    key: "grow",
    num: "03",
    title: "Grow and improve",
    blurb:
      "Invest in what comes next — new income, secured funding and the major projects that shape a club for years to come.",
    examples: [
      "Grant readiness & bid writing",
      "Sponsorship & new revenue",
      "Major-project feasibility",
      "Grounds & clubhouse improvements",
      "Accessibility & inclusion planning",
    ],
    image: IMAGES.grow,
  },
];

export const SERVICES = [
  "Energy procurement & utility reviews",
  "Insurance & supplier reviews",
  "Digital strategy & website support",
  "Data protection & cybersecurity",
  "Facility usage & booking optimisation",
  "Procurement & contract negotiation",
  "Governance & policy reviews",
  "Incorporation & legal structure",
  "Financial forecasting & business plans",
  "Grant readiness & bid writing",
  "Sponsorship packages & sales materials",
  "Volunteer recruitment & onboarding",
  "Club communications & engagement",
  "Major-project feasibility & delivery",
  "Accessibility, inclusion & environment",
];

export const VOLUNTEER = {
  overline: "Volunteer support",
  title: "Give volunteers more time to make a difference",
  body:
    "The SportMate.ai app supports mini and junior coaches, managers and other volunteers with practical tools, guidance and resources — helping reduce their workload while giving every child the best possible sporting experience.",
  points: [
    "Practical matchday & training prep",
    "Role-specific guidance and checklists",
    "Ready-to-use templates & messages",
    "Club info, resources & safeguarding in one place",
  ],
  cta: { label: "Visit the volunteer app", href: APP_URL },
};

export const ADVISORY = {
  overline: "Our approach",
  title: "Real-world experience, applied to your club",
  body:
    "It isn't simply that we can recommend software or review a bill. It's the combined experience of professionals who understand technology, finance, operations, projects and the realities of community sport.",
  points: [
    {
      t: "Experienced professionals",
      d: "Lawyers, IT and cyber-security leaders, CEOs, founders, CFOs and more — on your side.",
    },
    {
      t: "Practical & independent",
      d: "Straight advice that fits your club, never tied to any single supplier or product.",
    },
    {
      t: "Your first conversation is free",
      d: "A no-obligation, information-gathering review to understand where you are today.",
    },
    {
      t: "How an engagement works",
      d: "Talk → review → a clear, practical plan → optional hands-on delivery support.",
    },
    {
      t: "Clubs of every kind",
      d: "From small community teams to large multi-sport clubs, across all sports.",
    },
  ],
};

export const OUTCOMES = {
  overline: "What good looks like",
  title: "The outcomes we help clubs reach",
  note: "Detailed case studies coming soon — real results, no invented numbers.",
  items: [
    { t: "Costs saved", d: "Lower bills on energy, insurance and suppliers through honest reviews." },
    { t: "Grants secured", d: "Better bids and readiness to unlock the funding clubs are entitled to." },
    { t: "Volunteer hours reduced", d: "Less repetitive admin, so people spend time on what matters." },
    { t: "Projects delivered", d: "From clubhouse upgrades to floodlights — planned and seen through." },
    { t: "Clubs supported", d: "Practical help across finance, tech, grounds and governance." },
    { t: "Better experiences", d: "Smoother matchdays and communications for members and families." },
  ],
};

export const FINAL_CTA = {
  overline: "Where next",
  title: ["Not sure where", "your club needs", "help most?"],
  body:
    "Tell us what's taking up your time, costing too much or holding the club back. We'll help you identify the next practical step.",
  cta: { label: "Talk to SportMate.ai", type: "mail" },
};
